// ── Stop numbers on the Delivery Map ────────────────────────────────────────
// (client ask, 2026-09-25; the rules below are the dispatch team's answers the
// same day)
//
// An order's red tag reads "4-K9": stop 4 of driver K9. The tag is
// Order.priority — free text, at most 5 characters, uppercased at write time
// — so a dispatcher can still type anything; these helpers only understand
// the "<number>-<driver>" shape and leave any other tag alone.
//
//  - The count restarts every day on its own: only the driver's CURRENT
//    undelivered orders count.
//  - Assigning from the map pre-fills the next stop for that driver.
//  - When an order leaves a driver (unassigned, or moved to another driver)
//    the driver's remaining stops close the gap: 1, 2, 3 with nothing missing.
//  - Unassigning a driver clears the tag.
//  - Dispatch sets the number; the driver's text message names the stop.
//  - The driver's app listing by stop is a later step (needs an app release).

import { OrderStatus, type Order } from '../types';

export const TAG_MAX = 5;

/** A driver's code as it appears in a tag: the first word of the name with
 *  letters and digits only, in capitals — "K9 🛻 🔑" → "K9", "AI 🛻 🔑" → "AI". */
export const driverCode = (name: string | null | undefined): string => {
  for (const word of (name || '').split(/\s+/)) {
    const code = word.replace(/[^\p{L}\p{N}]/gu, '').toUpperCase();
    if (code) return code.slice(0, 3);
  }
  return '';
};

export interface StopTag {
  n: number;
  code: string;
}

/** "4-K9" → { n: 4, code: 'K9' }; free text, a bare number or nothing → null. */
export const parseStopTag = (priority: string | number | null | undefined): StopTag | null => {
  const m = String(priority ?? '').trim().toUpperCase().match(/^(\d{1,3})-([A-Z0-9]{1,3})$/);
  return m ? { n: Number(m[1]), code: m[2] } : null;
};

/** "4-K9". The code gives way so the whole tag fits the column: stop 12 of a
 *  driver called SAM is "12-SA". */
export const stopTag = (n: number, code: string): string => {
  const num = String(Math.max(1, Math.floor(n)));
  const c = code.toUpperCase().slice(0, Math.max(0, TAG_MAX - num.length - 1));
  return c ? `${num}-${c}` : num;
};

/** "stop 4" for a text message; '' for a tag that is not a stop. */
export const stopPhrase = (tag: string | null | undefined): string => {
  const s = parseStopTag(tag);
  return s ? `stop ${s.n}` : '';
};

/** An order still to be delivered — the only kind that counts as a stop. */
export const STOP_STATUSES: readonly OrderStatus[] = [OrderStatus.PENDING, OrderStatus.ASSIGNED, OrderStatus.CONFIRMED, OrderStatus.PICKED_UP];
const OPEN = new Set<string>(STOP_STATUSES);

export type StopOrder = Pick<Order, 'id' | 'driverId' | 'status' | 'priority' | 'createdAt'>;

/** The driver's current stops, lowest number first (same number: oldest order first). */
export const driverStops = <T extends StopOrder>(orders: readonly T[], driverId: string): Array<{ order: T; stop: StopTag }> =>
  orders
    .filter((o) => o.driverId === driverId && OPEN.has(o.status))
    .map((order) => ({ order, stop: parseStopTag(order.priority) }))
    .filter((x): x is { order: T; stop: StopTag } => x.stop !== null)
    .sort((a, b) => a.stop.n - b.stop.n || (a.order.createdAt || 0) - (b.order.createdAt || 0));

/** The next stop for a driver: one past their highest current stop (1 with none). */
export const nextStopNumber = (orders: readonly StopOrder[], driverId: string): number =>
  driverStops(orders, driverId).reduce((m, s) => Math.max(m, s.stop.n), 0) + 1;

/** The tag to save when the driver called `driverName` gets one more order. */
export const nextStopTag = (orders: readonly StopOrder[], driverId: string, driverName: string | null | undefined): string =>
  stopTag(nextStopNumber(orders, driverId), driverCode(driverName));

/** After `leavingOrderId` leaves the driver: the writes that renumber the
 *  driver's remaining stops 1..n in their current order. Only tags that
 *  change are listed; free-text tags are left alone. */
export const closeGapPlan = (
  orders: readonly StopOrder[],
  driverId: string,
  leavingOrderId: string,
  driverName?: string | null,
): Array<{ orderId: string; priority: string }> => {
  const code = driverCode(driverName);
  return driverStops(orders.filter((o) => o.id !== leavingOrderId), driverId)
    .map(({ order, stop }, i) => ({
      orderId: order.id,
      priority: stopTag(i + 1, code || stop.code),
      before: String(order.priority ?? '').trim().toUpperCase(),
    }))
    .filter((p) => p.priority !== p.before)
    .map(({ orderId, priority }) => ({ orderId, priority }));
};
